'use client';

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getEmpAppUsages } from '../../../redux/MainAction';
import Aux from '../../../hoc/Aux_';
import moment from 'moment';
import {
  FiClock,
  FiCalendar,
  FiMonitor,
  FiSearch,
  FiPieChart,
  FiFilter,
  FiBarChart2,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
} from 'react-icons/fi';
import Chart from 'react-apexcharts';
import { LuArrowUpDown } from 'react-icons/lu';

const AppUsage = () => {
  const dispatch = useDispatch();
  const [dateFilter, setDateFilter] = useState('today');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'duration',
    direction: 'desc',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [appUsagesData, setAppUsagesData] = useState([]);

  const appUsages = useSelector((state) => state.Webtracker.employeeAppUsage);

  const getSystemTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  const getDateRange = (filter) => {
    const endDate = moment().format('YYYY-MM-DD');
    let startDate;

    switch (filter) {
      case 'today':
        startDate = moment().format('YYYY-MM-DD');
        break;
      case '7days':
        startDate = moment().subtract(7, 'days').format('YYYY-MM-DD');
        break;
      case '30days':
        startDate = moment().subtract(30, 'days').format('YYYY-MM-DD');
        break;
      case '90days':
        startDate = moment().subtract(90, 'days').format('YYYY-MM-DD');
        break;
      default:
        startDate = moment().format('YYYY-MM-DD');
    }

    return { startDate, endDate };
  };

  useEffect(() => {
    getList();
  }, [dateFilter]);

  // Reset data when selecting "today" filter
  useEffect(() => {
    if (dateFilter === 'today') {
      // Force refresh data for today
      setAppUsagesData([]);
      getList();
    }
  }, [dateFilter]);

  const getList = () => {
    const { startDate, endDate } = getDateRange(dateFilter);

    dispatch(
      getEmpAppUsages({
        ownerId: localStorage.getItem('ownerId'),
        startDate,
        endDate,
        timezone: getSystemTimezone(),
      })
    );
  };

  useEffect(() => {
    if (appUsages && appUsages.length > 0) {
      setAppUsagesData(appUsages);
    }
  }, [appUsages]);

  const formatDuration = (seconds) => {
    if (!seconds) return '0 sec';

    if (seconds < 60) {
      return `${seconds} sec`;
    } else if (seconds < 3600) {
      return `${Math.floor(seconds / 60)} min ${seconds % 60} sec`;
    } else if (seconds < 86400) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours} hr ${minutes} min`;
    } else {
      const days = Math.floor(seconds / 86400);
      const hours = Math.floor((seconds % 86400) / 3600);
      return `${days} day${days > 1 ? 's' : ''} ${hours} hr`;
    }
  };

  const formatDate = (dateString) => {
    return moment(dateString).format('MMM DD, YYYY h:mm A');
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = () => {
    const filteredData = appUsagesData.filter(
      (app) =>
        app.name && app.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return [...filteredData].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const sortedData = getSortedData();

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  const chartData = {
    options: {
      chart: {
        id: 'app-usage-chart',
        toolbar: {
          show: false,
        },
        fontFamily: 'inherit',
      },
      xaxis: {
        categories: sortedData.slice(0, 5).map((app) => app.name || 'Unknown'),
        labels: {
          style: {
            fontSize: '12px',
            fontFamily: 'inherit',
          },
        },
      },
      colors: ['#6f42c1'],
      plotOptions: {
        bar: {
          borderRadius: 6,
          horizontal: true,
          barHeight: '70%',
          distributed: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        y: {
          formatter: (val) => formatDuration(val),
        },
        style: {
          fontSize: '12px',
          fontFamily: 'inherit',
        },
      },
      grid: {
        borderColor: '#f1f1f1',
        strokeDashArray: 4,
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
      },
    },
    series: [
      {
        name: 'Duration',
        data: sortedData.slice(0, 5).map((app) => app.duration || 0),
      },
    ],
  };

  const calculateSummary = () => {
    if (!sortedData.length)
      return {
        totalApps: 0,
        totalDuration: 0,
        totalSessions: 0,
        mostUsed: null,
      };

    const totalDuration = sortedData.reduce(
      (sum, app) => sum + (app.duration || 0),
      0
    );
    const totalSessions = sortedData.reduce(
      (sum, app) => sum + (app.sessions || 0),
      0
    );

    let mostUsed = null;
    if (sortedData.length > 0) {
      mostUsed = sortedData.reduce(
        (max, app) =>
          !max || (app.duration || 0) > (max.duration || 0) ? app : max,
        null
      );
    }

    return {
      totalApps: sortedData.length,
      totalDuration,
      totalSessions,
      mostUsed,
    };
  };

  const summary = calculateSummary();

  return (
    <Aux>
      <div className="page-content-wrapper chat-box-container">
        <div className="card shadow-sm">
          <div className="card-body p-3">
            <div className="d-flex flex-column flex-md-row justify-content-end align-items-start align-items-md-center mb-4">
              <div className="position-relative mr-3">
                <input
                  type="text"
                  placeholder="Search apps..."
                  className="form-control py-2 ps-3 rounded-3 border-secondary-subtle"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <FiSearch
                  className="position-absolute text-muted"
                  style={{ right: '12px', top: '12px' }}
                  size={16}
                />
              </div>

              <div className="position-relative">
                <button
                  className="btn btn-light d-flex align-items-center justify-content-between w-100 py-2 px-3 rounded-3 border-secondary-subtle"
                  style={{ minWidth: '180px' }}
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                >
                  <span className="d-flex align-items-center">
                    <FiCalendar className="mr-2 text-primary" size={16} />
                    {dateFilter === 'today'
                      ? 'Today'
                      : dateFilter === '7days'
                        ? 'Last 7 days'
                        : dateFilter === '30days'
                          ? 'Last 30 days'
                          : 'Last 90 days'}
                  </span>
                  <FiChevronDown size={16} />
                </button>

                {isFilterOpen && (
                  <div
                    className="position-absolute mt-1 w-100 bg-white border rounded-3 shadow-sm py-1"
                    style={{ zIndex: 1000 }}
                  >
                    <ul className="list-unstyled m-0 p-0">
                      <li
                        className={`px-3 py-2 ${dateFilter === 'today' ? 'bg-light text-primary' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setDateFilter('today');
                          setIsFilterOpen(false);
                        }}
                      >
                        Today
                      </li>
                      <li
                        className={`px-3 py-2 ${dateFilter === '7days' ? 'bg-light text-primary' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setDateFilter('7days');
                          setIsFilterOpen(false);
                        }}
                      >
                        Last 7 days
                      </li>
                      <li
                        className={`px-3 py-2 ${dateFilter === '30days' ? 'bg-light text-primary' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setDateFilter('30days');
                          setIsFilterOpen(false);
                        }}
                      >
                        Last 30 days
                      </li>
                      <li
                        className={`px-3 py-2 ${dateFilter === '90days' ? 'bg-light text-primary' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setDateFilter('90days');
                          setIsFilterOpen(false);
                        }}
                      >
                        Last 90 days
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="row g-4 mb-4">
              <div className="col-md-6 col-lg-3 mb-3">
                <div className="card h-100 bg-light border-0 rounded-4 shadow-sm">
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-start">
                      <div style={{ maxWidth: '80%' }}>
                        <p className="text-primary mb-1 fw-medium small">
                          Total Applications
                        </p>
                        <h4 className="mb-0 text-truncate">
                          {summary.totalApps}
                        </h4>
                      </div>
                      <div>
                        <FiMonitor size={22} className="text-primary" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-3 mb-3">
                <div className="card h-100 bg-light border-0 rounded-4 shadow-sm">
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-start">
                      <div style={{ maxWidth: '80%' }}>
                        <p className="text-purple mb-1 fw-medium small">
                          Total Usage Time
                        </p>
                        <h4 className="fs-4 fw-bold mb-0 text-truncate">
                          {formatDuration(summary.totalDuration)}
                        </h4>
                      </div>
                      <div>
                        <FiClock size={22} className="text-purple" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-3 mb-3">
                <div className="card h-100 bg-light border-0 rounded-4 shadow-sm">
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-start">
                      <div style={{ maxWidth: '80%' }}>
                        <p className="text-danger mb-1 fw-medium small">
                          Total Sessions
                        </p>
                        <h4 className="fs-4 fw-bold mb-0 text-truncate">
                          {summary.totalSessions}
                        </h4>
                      </div>
                      <div>
                        <FiFilter size={22} className="text-danger" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-3 mb-3">
                <div className="card h-100 bg-light border-0 rounded-4 shadow-sm">
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-start">
                      <div style={{ maxWidth: '80%' }}>
                        <p className="text-success mb-1 fw-medium small">
                          Most Used App
                        </p>
                        <h5 className="fw-bold mb-0 text-truncate">
                          {summary.mostUsed?.name || 'N/A'}
                        </h5>
                        <p className="small text-success mt-1 mb-0">
                          {summary.mostUsed
                            ? formatDuration(summary.mostUsed.duration)
                            : ''}
                        </p>
                      </div>
                      <div>
                        <FiPieChart size={22} className="text-success" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm rounded-4 mb-4 bg-white">
              <div className="card-body">
                <h5 className="card-title mb-4 fw-bold">
                  Top 5 Applications by Usage
                </h5>
                <div style={{ height: '300px' }}>
                  {typeof window !== 'undefined' && (
                    <Chart
                      options={chartData.options}
                      series={chartData.series}
                      type="bar"
                      height="100%"
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm rounded-4 p-0 overflow-hidden">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th
                          className="py-3 px-4 border-0"
                          onClick={() => requestSort('name')}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center">
                            Application
                            <LuArrowUpDown className="ms-2" size={16} />
                          </div>
                        </th>
                        <th
                          className="py-3 px-4 border-0"
                          onClick={() => requestSort('duration')}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center">
                            Duration
                            <LuArrowUpDown className="ms-2" size={16} />
                          </div>
                        </th>
                        <th
                          className="py-3 px-4 border-0 d-none d-md-table-cell"
                          onClick={() => requestSort('sessions')}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center">
                            Sessions
                            <LuArrowUpDown className="ms-2" size={16} />
                          </div>
                        </th>
                        <th
                          className="py-3 px-4 border-0 d-none d-lg-table-cell"
                          onClick={() => requestSort('lastUsed')}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center">
                            Last Used
                            <LuArrowUpDown className="ms-2" size={16} />
                          </div>
                        </th>
                        <th
                          className="py-3 px-4 border-0 d-none d-xl-table-cell"
                          onClick={() => requestSort('firstUsed')}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center">
                            First Used
                            <LuArrowUpDown className="ms-2" size={16} />
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map((app, index) => (
                        <tr key={index}>
                          <td className="py-3 px-4">
                            <div
                              className="d-flex align-items-center"
                              style={{ gap: '6px' }}
                            >
                              <div
                                className="d-flex align-items-center justify-content-center bg-primary rounded-circle text-white"
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  fontSize: '16px',
                                }}
                              >
                                {app.name
                                  ? app.name.charAt(0).toUpperCase()
                                  : '?'}
                              </div>
                              <div className="ms-3">
                                <div className="fw-medium">
                                  {app.name || 'Unknown'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="d-flex align-items-center">
                              <FiClock
                                className="mr-2 text-primary"
                                size={18}
                              />
                              <span className="fw-medium">
                                {formatDuration(app.duration)}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4 d-none d-md-table-cell">
                            <div className="d-flex align-items-center">
                              <FiBarChart2
                                className="mr-2 text-primary"
                                size={18}
                              />
                              <span className="fw-medium">
                                {app.sessions || 0}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4 d-none d-lg-table-cell text-muted">
                            {app.lastUsed ? formatDate(app.lastUsed) : 'N/A'}
                          </td>
                          <td className="py-3 px-4 d-none d-xl-table-cell text-muted">
                            {app.firstUsed ? formatDate(app.firstUsed) : 'N/A'}
                          </td>
                        </tr>
                      ))}

                      {currentItems.length === 0 && (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center py-4 text-muted"
                          >
                            No applications found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {sortedData.length > itemsPerPage && (
                <div className="d-flex justify-content-between align-items-center border-top p-4">
                  <div className="d-none d-sm-block">
                    <p className="text-muted mb-0">
                      Showing{' '}
                      <span className="fw-medium">{indexOfFirstItem + 1}</span>{' '}
                      to{' '}
                      <span className="fw-medium">
                        {indexOfLastItem > sortedData.length
                          ? sortedData.length
                          : indexOfLastItem}
                      </span>{' '}
                      of <span className="fw-medium">{sortedData.length}</span>{' '}
                      results
                    </p>
                  </div>
                  <nav aria-label="Page navigation">
                    <ul className="pagination mb-0">
                      <li
                        className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}
                      >
                        <button
                          className="page-link rounded-start"
                          onClick={() =>
                            setCurrentPage((prev) => Math.max(prev - 1, 1))
                          }
                          disabled={currentPage === 1}
                        >
                          <FiChevronLeft size={16} />
                        </button>
                      </li>

                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <li
                            key={page}
                            className={`page-item ${page === currentPage ? 'active' : ''}`}
                          >
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </button>
                          </li>
                        )
                      )}

                      <li
                        className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}
                      >
                        <button
                          className="page-link rounded-end"
                          onClick={() =>
                            setCurrentPage((prev) =>
                              Math.min(prev + 1, totalPages)
                            )
                          }
                          disabled={currentPage === totalPages}
                        >
                          <FiChevronRight size={16} />
                        </button>
                      </li>
                    </ul>
                  </nav>

                  <div className="d-flex d-sm-none justify-content-between w-100">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                      className={`btn btn-sm ${currentPage === 1 ? 'btn-light text-muted' : 'btn-outline-primary'}`}
                    >
                      Previous
                    </button>
                    <span className="text-muted d-flex align-items-center fw-medium">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                      className={`btn btn-sm ${currentPage === totalPages ? 'btn-light text-muted' : 'btn-outline-primary'}`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Aux>
  );
};

export default AppUsage;
